import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid2,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { useField, useFormikContext } from 'formik';
import CloseIcon from '@mui/icons-material/CloseRounded';
import InputWrapper from '../InputWrapper';
import {
  important,
  isValidFileType,
  sxToObject,
} from '../../../../utils/global/helper';
import { getStorageUrl } from '../../../../helpers/global';

interface FileInputProps {
  name: string;
  label: string;
  disabled?: boolean;
  tooltip?: string;
  accept?: string[];
  loading?: boolean;
  acceptLabel?: string;
  acceptLabelNewLine?: boolean;
  acceptLabelSx?: SxProps<Theme>;
  required?: boolean;
}

function FileItem({
  file,
  name,
  disabled,
}: {
  file: string | File;
  name: string;
  disabled?: boolean;
}) {
  const { values, setFieldValue } = useFormikContext();

  const getDisplayName = () => {
    let displayName = '';
    try {
      if (typeof file === 'string') {
        const nameFromData = decodeURIComponent(new URL(file).pathname)
          .split('/')
          .pop();
        displayName = nameFromData || file;
      } else {
        displayName = file.name;
      }
    } catch {
      displayName = typeof file === 'string' ? file : file.name;
    }
    const [fileName, extension] = displayName.split('.');
    if (fileName.length > 16) {
      return `${fileName.slice(0, 16)}...${extension}`;
    }
    return displayName;
  };

  const onRemove = () => {
    const filesValue = values?.[name as keyof typeof values] as unknown as
      | string
      | File
      | null;
    if (!filesValue) return;

    setFieldValue(name, null);
  };

  const onOpenFile = async () => {
    const urlString =
      typeof file === 'string' ? file : URL.createObjectURL(file);
    // bust cache
    let url = new URL(urlString);
    if (typeof file === 'string') {
      url = new URL((await getStorageUrl(urlString)) || urlString);
      url.searchParams.set('t', Date.now().toString());
    }
    window.open(url);
  };

  return (
    <Grid2
      size={{
        xs: 3,
      }}
      width="100%"
      style={{ paddingTop: 0 }}>
      <Box
        height="fit-content"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        borderRadius="4px"
        border={theme => `1px solid ${theme.palette.lightOutlineGrey.main}`}
        bgcolor={theme => theme.palette.lightGrey.main}>
        <Button
          type="button"
          variant="text"
          sx={() => ({
            width: '100%',
            paddingY: 0.5,
            paddingX: 1,
            margin: 0,
            minWidth: 0,
            justifyContent: 'flex-start',
          })}
          onClick={onOpenFile}>
          <Typography
            noWrap
            textAlign="left"
            fontSize={theme => theme.typography.body2.fontSize}
            variant="body1">
            {getDisplayName()}
          </Typography>
        </Button>
        <Divider
          orientation="vertical"
          flexItem
          sx={theme => ({
            borderColor: theme.palette.lightOutlineGrey.main,
            ml: 'auto',
          })}
        />
        <Button
          disabled={disabled}
          type="button"
          onClick={onRemove}
          variant="text"
          sx={() => ({
            color: '#000000',
            paddingY: 0.5,
            paddingX: 1,
            margin: 0,
            minWidth: 0,
            minHeight: 0,
          })}>
          <CloseIcon
            sx={{
              fontSize: '12px',
            }}
          />
        </Button>
      </Box>
    </Grid2>
  );
}
/**
 * File input component - Only supports single file upload for now
 */
function FileInput({
  name,
  label,
  disabled,
  tooltip,
  accept,
  loading,
  acceptLabel,
  acceptLabelNewLine,
  acceptLabelSx,
  required,
}: FileInputProps) {
  const theme = useTheme();
  const [{ value }, { touched, error }, { setValue, setTouched }] =
    useField(name);

  const isError = touched && !!error;
  const inputRef = React.useRef<HTMLInputElement>(null);

  const filesValue = value as unknown as string | string[] | null;
  const isDisabled = disabled || !!filesValue;

  const onClick = () => {
    if (isDisabled) return;
    inputRef.current?.click();
  };

  const onInputFile = (files: FileList) => {
    if (isDisabled) return;
    const filesArray = Array.from(files);
    const filteredFiles = filesArray.filter(file => {
      if (accept) {
        return isValidFileType(file, accept);
      }
      return true;
    });
    if (!filteredFiles.length) return;

    setValue(filteredFiles[0], true);
    if (inputRef.current) inputRef.current.value = '';
    // setTouched after setValue to avoid validation error
    setTimeout(() => setTouched(true, true), 0);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    onInputFile(e.dataTransfer.files);
  };

  return (
    <InputWrapper
      label={label}
      name={name}
      tooltip={tooltip}
      required={required}>
      <Grid2
        marginTop={0}
        onDragOver={e => e.preventDefault()}
        onDrop={onDrop}
        onClick={onClick}
        height="100px"
        boxSizing="border-box"
        paddingY={filesValue ? 2 : 0}
        paddingLeft={filesValue ? 1 : 0}
        paddingRight={filesValue ? 2 : 0}
        marginLeft={0}
        position="relative"
        container
        columns={12}
        spacing={1}
        border={important(
          `1px dashed ${
            isError
              ? theme.palette.error.main
              : theme.palette.lightOutlineGrey.main
          }`,
          isError,
        )}
        sx={{
          width: '100%',
          overflowY: 'auto',
          borderRadius: '10px',
          ':hover': {
            backgroundColor: 'transparent',
            border: isDisabled ? undefined : `1px dashed #000000`,
          },
          ':active': isDisabled
            ? undefined
            : {
                backgroundColor: 'transparent',
                border: `2px solid ${theme.palette.primary.main}`,
                transition: theme.transitions.create('border', {
                  duration: theme.transitions.duration.shortest,
                }),
              },
        }}>
        {!filesValue && !loading && (
          <Box
            position="absolute"
            left={0}
            right={0}
            top={0}
            bottom={0}
            display="flex"
            alignItems="center"
            justifyContent="center">
            <Typography
              variant="body1"
              fontWeight="700"
              sx={{
                fontSize: theme.typography.pxToRem(14),
                textDecoration: 'underline',
                color: '#4A6BA6',
                opacity: isDisabled ? 0.8 : 1,
                ...sxToObject(acceptLabelSx, theme),
              }}>
              Drag and drop or click to upload
              {acceptLabelNewLine && <br />}
              {acceptLabel ? ` (${acceptLabel})` : ''}
            </Typography>
          </Box>
        )}
        {filesValue && !loading && (
          <FileItem
            name={name}
            file={filesValue as string}
            disabled={disabled}
          />
        )}
        {loading && (
          <Box
            position="absolute"
            left={0}
            right={0}
            top={0}
            bottom={0}
            display="flex"
            alignItems="center"
            justifyContent="center">
            <CircularProgress size={24} />
          </Box>
        )}
      </Grid2>
      <input
        ref={inputRef}
        style={{
          display: 'none',
        }}
        type="file"
        accept={accept?.join(',')}
        multiple={false}
        disabled={isDisabled}
        onChange={e => onInputFile(e.target.files as FileList)}
      />
    </InputWrapper>
  );
}

export default FileInput;
